import {assert} from 'Asserts';
import {
  Container,
  ContainerSummary,
  GridCellSummary,
  SampleDetailsWithSchema,
} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {makeBioentitySampleDetails} from 'notebook/sample-propagation/util/makeSampleDetails';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {ContainersColumnMode} from 'notebook/transfer-tables/TransferTableConstants';
import {getParentStorableFromAncestralStorables} from 'samples/storables/ParentStorableUtil';
import {ParentStorableType} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  PreparedSample,
  PreparedSampleGroup,
  PreparedUISampleGroup,
} from 'workflows/samples/PreparedSampleGroupConstants';
import {SampleGroupSchema} from 'workflows/WorkflowConstants';

import {ContainerGraphQL} from '../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

function getSample(samples: Array<PreparedSample>): PreparedSample | null {
  assert(samples.length <= 1, 'Expected at most 1 sample per field per sample group');
  return samples.length > 0 ? samples[0] : null;
}

export function getSampleForField(
  sampleGroup: PreparedSampleGroup | PreparedUISampleGroup | null,
  sampleGroupField: SchemaField
): PreparedSample | null {
  const tag =
    sampleGroup && sampleGroup.tags.find((preparedTag) => preparedTag.name === sampleGroupField.name);
  return tag && tag.targetSamples && tag.targetSamples.length > 0 ? getSample(tag.targetSamples) : null;
}

export function getContainerIds(
  sampleGroupsWithSchemas: Array<{
    sampleGroup: PreparedSampleGroup | PreparedUISampleGroup;
    sampleGroupSchema: SampleGroupSchema;
  }>
): Array<string> {
  let samples: Array<PreparedSample | null> = [];
  for (const {sampleGroup, sampleGroupSchema} of sampleGroupsWithSchemas) {
    samples = [...samples, ...sampleGroupSchema.fields.map((field) => getSampleForField(sampleGroup, field))];
  }
  return samples
    .map((sample) => (sample == null ? null : sample.containerId))
    .filter((containerId) => containerId != null);
}

export function getContainerForSample({
  sample,
  containersById,
}: {
  sample: PreparedSample;
  containersById: {[containerId: string]: Container | ContainerGraphQL | null};
}): Container | ContainerGraphQL | null {
  if (sample.containerId == null) {
    return null;
  }
  if (containersById[sample.containerId] == null) {
    return null;
  }
  return containersById[sample.containerId];
}

export function getContainerForSampleFromREST({
  sample,
  containersById,
}: {
  sample: PreparedSample;
  containersById: {[containerId: string]: Container | null};
}): Container | null {
  return getContainerForSample({sample, containersById}) as Container;
}

export function getContainerLocation({
  ancestralStorablesById,
  container,
}: {
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  container: ContainerSummary | null;
}): StorableSummaryGraphQL | null {
  if (container == null) {
    return null;
  }
  return container && ancestralStorablesById[container.id]
    ? getParentStorableFromAncestralStorables(container, ancestralStorablesById[container.id])
    : null;
}

export function getContainerGridCell({
  sample,
  containersById,
}: {
  sample: PreparedSample;
  containersById: {[containerId: string]: Container | ContainerGraphQL | null};
}): GridCellSummary | null {
  const container = getContainerForSample({sample, containersById});
  if (container == null || container.gridCell == null) {
    return null;
  }
  const gridCell = container.gridCell;
  if ('__typename' in gridCell) {
    return !isInaccessible(gridCell) ? gridCell : null;
  }
  return gridCell;
}

interface ContainerColumn extends ColumnConstraint {
  columnDefinition: ColumnDefinition;
  renderedLabel: string;
}

/**
 * Determine what spreadsheet columns to show for a sample group field's containers.
 *
 * There's 3 possible columns, Container, Location, and Position. To avoid making tables too wide,
 * each column is only included if it has data.
 *
 * For example:
 * - If a sample group field has no containers, none of the columns will be included
 * - If a field has containers, but none of the containers are in grids,
 *   the Container column will be included but not the Position column.
 */
export function getContainerColumnsForSampleGroupField({
  sampleGroups,
  sampleGroupField,
  containersById,
  ancestralStorablesById,
}: {
  sampleGroups: Array<PreparedSampleGroup | PreparedUISampleGroup>;
  sampleGroupField: SchemaField;
  containersById: {[containerId: string]: Container | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
}): Array<ContainerColumn> {
  const extraColumnConstraints: Array<ContainerColumn> = [];

  let hasAnyContainer = false;
  let hasAnyLocation = false;
  let hasAnyPosition = false;

  for (const sampleGroup of sampleGroups) {
    const sample = getSampleForField(sampleGroup, sampleGroupField);
    if (sample?.containerId != null) {
      hasAnyContainer = true;

      const container = getContainerForSampleFromREST({sample, containersById});
      const location = getContainerLocation({ancestralStorablesById, container});
      if (location != null) {
        hasAnyLocation = true;
        if (getContainerGridCell({sample, containersById}) != null) {
          hasAnyPosition = true;
        }
      }
    }
  }

  if (hasAnyContainer) {
    extraColumnConstraints.push({
      columnDefinition: {
        type: TableColumnType.CONTAINERS,
        linkedSampleSchemaField: sampleGroupField,
        isRequired: false,
        containersColumnMode: ContainersColumnMode.LEGACY,
      },
      renderedLabel: 'Container',
    });
  }
  if (hasAnyLocation) {
    extraColumnConstraints.push({
      columnDefinition: {
        type: TableColumnType.PARENT_STORABLE,
        isForTransfer: false,
        linkedSampleSchemaField: sampleGroupField,
        isRequired: false,
        parentStorableType: ParentStorableType.GRID_OR_LOCATION,
      },
      renderedLabel: 'Location',
    });
  }
  if (hasAnyPosition) {
    extraColumnConstraints.push({
      columnDefinition: {
        type: TableColumnType.POSITION,
        linkedSampleSchemaField: sampleGroupField,
        isRequired: false,
      },
      renderedLabel: 'Position',
    });
  }

  return extraColumnConstraints;
}

export function getSampleDetailsForBioentities(
  bioentities: Array<BioentitySummary>
): Array<SampleDetailsWithSchema> {
  return bioentities.map((bioentity) => ({
    ...makeBioentitySampleDetails({
      id: bioentity.id,
      name: bioentity.name,
    }),
    schema: bioentity.tagSchema,
  }));
}
